import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession, useUser } from "@clerk/react";
import { Layout, SupportIcon } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { useConsumerIdentityStore } from "../store/useConsumerIdentityStore";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import {
  useOperationSSE,
  upsertOperationInList,
} from "../hooks/useOperationSSE";
import { ConsumerSessionMenu } from "../components/ConsumerSessionMenu";
import { CurrentExchangeRateCard } from "../components/CurrentExchangeRateCard";
import { TransactionsHistory } from "../components/TransactionsHistory";
import { bindConsumerIdentity, listOperations } from "../services/api";
import type { IOperationResponse } from "../services/api";
import type { ITransaction } from "../interfaces/transactions";
import { APP_ROUTES } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { useHomeExchangeRate } from "../hooks/useHomeExchangeRate";
import { amountFormatter } from "../utils";
import { homeGreetingName } from "../utils/homeGreeting";
import { isCashDepositPending } from "../utils/cashDeposit";
import { useLaunchCashDepositPayment } from "../hooks/useLaunchCashDepositPayment";
import { getEvents, safeCapture } from "../analytics";
import { NotificationsBell } from "../components/notifications/NotificationsBell";
import { useNovuInbox } from "../components/notifications/NovuInboxProvider";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { Navbar } from "../components/Navbar";

/** How many operations are fetched per page as the user scrolls down. */
const PAGE_SIZE = 8;

/** Pull distance (px) required to trigger a refresh on mobile. */
const PULL_THRESHOLD = 70;

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

/**
 * Home screen of the fund-request flow.
 *
 * Lists the user's money-transfer operations (most recent first) with infinite
 * scroll: pages are fetched lazily as a sentinel near the bottom of the list
 * enters the viewport. Reached when the user defers a request ("Later"/"Dismiss"
 * on the fund-request screen) and after an operation is created.
 */
export const TransactionsPage = memo(() => (
  <RequireActiveClerkSession resume={{ kind: "transactions" }}>
    <TransactionsPageContent />
  </RequireActiveClerkSession>
));

const TransactionsPageContent = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { user } = useUser();
  const { session: clerkSession } = useSession();
  const payerFirstName = useConsumerIdentityStore(
    (state) => state.bind?.payerFirstName,
  );
  const { redirectTo } = useRouteWithToken();

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("home"));
  }, []);

  const colorConfig = theme.whiteView;
  const inbox = useNovuInbox();
  const headerSlot = inbox?.headerSlot ?? "help";
  const showBell = headerSlot === "bell";
  const showHelp = headerSlot === "help";
  const { openSupport, supportSheet } = useSupportSheet();

  const userData = session.data?.userData;
  const requesterName =
    [userData?.name?.firstName, userData?.name?.lastName]
      .filter(Boolean)
      .join(" ") ||
    userData?.fullName ||
    t("fundRequest.someone");
  const clerkGreetingName = homeGreetingName({
    clerkFirstName: user?.firstName,
    clerkFullName: user?.fullName,
  });

  // Email sign-up leaves Clerk without a name. The login bind often happens
  // before CIP, and that result stays cached, so refresh once on this screen
  // when we still have nothing to show.
  const clerkSessionRef = useRef(clerkSession);
  clerkSessionRef.current = clerkSession;

  useEffect(() => {
    if (clerkGreetingName || payerFirstName) return;
    const sessionId = clerkSessionRef.current?.id;
    if (!sessionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await clerkSessionRef.current?.getToken();
        if (cancelled || !token) return;
        const bound = await bindConsumerIdentity(token);
        const name = bound.user.firstName?.trim();
        if (cancelled || !name) return;
        const current = useConsumerIdentityStore.getState().bind;
        if (current && current.clerkSessionId === sessionId && current.linkId) {
          useConsumerIdentityStore.getState().setBind({
            ...current,
            payerFirstName: name,
          });
        }
      } catch (error) {
        console.error("[TransactionsPage] payer name refresh failed:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clerkGreetingName, payerFirstName, clerkSession?.id]);

  const sessionFirstName = homeGreetingName({
    clerkFirstName: user?.firstName,
    clerkFullName: user?.fullName,
    cipFirstName: payerFirstName,
  });

  // Raw operations are kept (rather than pre-mapped transactions) so that live
  // SSE deltas can be applied by operationId and the derived rows recomputed.
  const [operations, setOperations] = useState<IOperationResponse[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [payingOperationId, setPayingOperationId] = useState<string | null>(
    null,
  );

  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const { launch: launchCashDeposit, launching: launchingCashDeposit } =
    useLaunchCashDepositPayment(session.data?.linkId);

  const handleDeposit = useCallback(
    async (operationId: string) => {
      safeCapture(getEvents().CASH_DEPOSIT_LAUNCH, {
        operation_id: operationId,
      });
      setPayingOperationId(operationId);
      try {
        await launchCashDeposit(operationId);
      } finally {
        setPayingOperationId(null);
      }
    },
    [launchCashDeposit],
  );

  const mapOperation = useCallback(
    (op: IOperationResponse): ITransaction => {
      const cashDepositPending = isCashDepositPending(op);
      const recipientName =
        [op.recipient?.name?.firstName, op.recipient?.name?.lastName]
          .filter(Boolean)
          .join(" ") || requesterName;
      return {
        operationId: op.operationId,
        status: op.status,
        amount: amountFormatter(op.sendAmount, op.sourceCurrency),
        currency: op.sourceCurrency,
        date: formatDate(op.createdAt),
        recipientName,
        cashDepositPending,
        depositName: cashDepositPending ? recipientName : undefined,
        launching:
          launchingCashDeposit && payingOperationId === op.operationId,
        onDeposit: cashDepositPending
          ? () => {
              void handleDeposit(op.operationId);
            }
          : undefined,
        onSelect: cashDepositPending
          ? undefined
          : () => redirectTo(`${APP_ROUTES.RECEIPT_PAGE}/${op.operationId}`),
      };
    },
    [requesterName, redirectTo, handleDeposit, launchingCashDeposit, payingOperationId]
  );

  const transactions = useMemo<ITransaction[]>(
    () => operations.map(mapOperation),
    [operations, mapOperation]
  );

  const { card: homeRate } = useHomeExchangeRate(operations, session.data);

  const loadNextPage = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId || loadingRef.current) return;

    loadingRef.current = true;
    const nextPage = pageRef.current + 1;

    try {
      const { items, pagination } = await listOperations(linkId, {
        page: nextPage,
        limit: PAGE_SIZE,
      });

      setOperations((prev) => (nextPage === 1 ? items : [...prev, ...items]));
      pageRef.current = nextPage;
      setHasMore(pagination ? pagination.hasMore : false);
    } catch (error) {
      console.error("[TransactionsPage] Failed to fetch operations:", error);
      setHasMore(false);
    } finally {
      loadingRef.current = false;
    }
  }, [session.data?.linkId]);

  // Reset to the first page (used by pull-to-refresh).
  const refresh = useCallback(async () => {
    pageRef.current = 0;
    setHasMore(true);
    await loadNextPage();
  }, [loadNextPage]);

  // Apply a live SSE delta to a single operation (status/funding changes).
  const upsertOperation = useCallback((op: IOperationResponse) => {
    setOperations((prev) => upsertOperationInList(prev, op));
  }, []);

  // After an SSE reconnection, re-fetch the most recent page and merge it so we
  // catch up on any deltas missed while the socket was down (non-terminal
  // operations are the most recent and live on the first page).
  const reconcileFromServer = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId) return;
    try {
      const { items } = await listOperations(linkId, {
        page: 1,
        limit: PAGE_SIZE,
      });
      setOperations((prev) =>
        items.reduce((acc, op) => upsertOperationInList(acc, op), prev)
      );
    } catch (error) {
      console.error(
        "[TransactionsPage] SSE reconnect reconcile failed:",
        error
      );
    }
  }, [session.data?.linkId]);

  // Keep operation statuses live while any loaded operation is non-terminal.
  useOperationSSE(
    session.data?.linkId,
    operations,
    upsertOperation,
    reconcileFromServer
  );

  const { pullDistance, refreshing, pulling } = usePullToRefresh(
    containerRef,
    refresh,
    { threshold: PULL_THRESHOLD }
  );

  // Initial page once the session (linkId) is available.
  useEffect(() => {
    if (session.data?.linkId && pageRef.current === 0) {
      void loadNextPage();
    }
  }, [session.data?.linkId, loadNextPage]);

  // Infinite scroll: load the next page when the bottom sentinel is revealed.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadNextPage();
      },
      { root: containerRef.current, rootMargin: "120px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, transactions.length, loadNextPage]);

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={
          showBell
            ? "need-help-header notifications-home-header"
            : "need-help-header"
        }
      >
        <Layout.Header>
          <ConsumerSessionMenu />
          {headerSlot === "pending" ? null : (
            <Navbar
              align="right"
              noBackButton
              title={showHelp ? t("common.needHelp") : undefined}
              onIconClick={showHelp ? () => openSupport() : undefined}
              icon={
                showBell ? (
                  <NotificationsBell />
                ) : (
                  <SupportIcon size={35} colors={colorConfig.dropzoneColors} />
                )
              }
            />
          )}
        </Layout.Header>
        <Layout.Content className="transactions-section">
          <TransactionsHistory
            items={transactions}
            greeting={
              sessionFirstName
                ? t("transactions.greeting", { name: sessionFirstName })
                : t("transactions.greetingPlain")
            }
            rateCard={
              homeRate ? (
                <CurrentExchangeRateCard
                  countryIso2={homeRate.countryIso2}
                  rate={homeRate.rate}
                  currency={homeRate.currency}
                  label={t("transactions.currentRate")}
                />
              ) : undefined
            }
            containerRef={containerRef}
            sentinelRef={sentinelRef}
            hasMore={hasMore}
            pullDistance={pullDistance}
            refreshing={refreshing}
            pulling={pulling}
            pullThreshold={PULL_THRESHOLD}
          />
        </Layout.Content>
      </AuraLayout>
      {showHelp ? supportSheet : null}
    </>
  );
});
